import type { FormValidateFn } from '@tanstack/react-form';
import type { SubmitEventHandler } from 'react';
import type { FormApi } from '../../form-hooks/useFormApi';

import { useStore } from '@tanstack/react-form';
import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import yaml from 'yaml';

import { ResponseError } from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Tooltip from '../../components/Tooltip/Tooltip';

import { useAppForm } from '../../form-hooks/form';

const DROPDOWN_OPTIONS = [
  { label: 'YAML', value: 'yaml' },
  { label: 'JSON', value: 'json' },
];

const yamlToJson = (yamlString: string) => {
  const parsedData = yaml.parse(yamlString);
  return JSON.stringify(parsedData, null, 2);
};

const jsonToYaml = (jsonString: string) => {
  const parsedData = JSON.parse(jsonString);
  return yaml.stringify(parsedData);
};

export interface PlainObjectEditorFormProps {
  onSubmit: (
    values: any,
  ) => Promise<[any, Record<string, string | string[]> | null, boolean]>;
  formState?: FormApi;
  defaultValue?: string;
  testId?: string;
}

const PlainObjectEditorForm = ({
  onSubmit,
  defaultValue,
  formState: objectFormState,
  testId,
}: PlainObjectEditorFormProps) => {
  const { t } = useTranslation();

  const validationSchema = useCallback<
    FormValidateFn<{ code: string; format: string }>
  >(
    ({ value }) => {
      const { format, code } = value;
      if (!code) return;

      try {
        if (format === 'yaml') {
          yaml.parse(code);
        } else {
          JSON.parse(code);
        }
      } catch {
        return {
          fields: {
            format: '',
            code:
              format === 'yaml'
                ? t('PlainObjectEditorForm.InvalidYAML')
                : t('PlainObjectEditorForm.InvalidJSON'),
          },
        };
      }
    },
    [t],
  );

  const form = useAppForm({
    defaultValues: { code: defaultValue || '', format: 'json' },
    onSubmit: async ({ value }) => {
      const { format, code } = value;
      if (!code) return;

      try {
        const json = format === 'yaml' ? yamlToJson(code) : code;
        const parsedData = JSON.parse(json);
        const [newObject, errors, hasErrors] = await onSubmit(parsedData);

        if (hasErrors) {
          throw new ResponseError('Response error', errors);
        }

        if (objectFormState?.resetForm) {
          objectFormState.resetForm(newObject, { keepDefaultValues: true });
        }
      } catch (error) {
        let errors: string | string[];
        if (error instanceof ResponseError) {
          errors =
            typeof error.errors === 'string'
              ? error.errors
              : Object.entries(error.errors).map(
                  ([field, messages]) =>
                    `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`,
                );
        } else {
          errors =
            format === 'yaml'
              ? t('PlainObjectEditorForm.InvalidYAML')
              : t('PlainObjectEditorForm.InvalidJSON');
        }
        form.setFieldMeta('code', (prev) => ({
          ...prev,
          errorMap: { onSubmit: errors },
          isTouched: true,
        }));
      }
    },
    validators: {
      onChange: validationSchema,
    },
  });

  useEffect(() => {
    if (!defaultValue) return;

    const format = form.getFieldValue('format');
    let code = defaultValue;

    if (format === 'yaml') {
      try {
        code = jsonToYaml(defaultValue);
      } catch {
        form.setFieldValue('format', 'json');
      }
    }
    form.setFieldValue('code', code);
  }, [defaultValue, form]);

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const disabled = isSubmitting || objectFormState?.isSubmitting;

  const onCopy = useCallback(() => {
    const code = form.getFieldValue('code');
    if (!code || objectFormState?.dirty) return;
    navigator.clipboard.writeText(code);
    toast.success(t('PlainObjectEditorForm.CopiedToClipboard'));
  }, [form, objectFormState?.dirty, t]);

  const onRestore = useCallback(() => {
    const format = form.getFieldValue('format');
    const resetValues =
      format === 'yaml' ? jsonToYaml(defaultValue || '') : defaultValue || '';
    form.setFieldValue('code', resetValues);
  }, [defaultValue, form]);

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="raw-object-editor"
        className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mt-2"
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId)}
      >
        <form.AppField
          name="format"
          listeners={{
            onChange: ({ value, fieldApi }) => {
              const code = fieldApi.form.getFieldValue('code');
              if (!code) return;

              try {
                let convertedValue = '';
                if (value === 'json') {
                  convertedValue = yamlToJson(code);
                } else {
                  convertedValue = jsonToYaml(code);
                }
                fieldApi.form.setFieldValue('code', convertedValue);
              } catch {
                // nothing to do, validation will handle showing the error message
              }
            },
          }}
        >
          {(field) => (
            <field.Dropdown
              options={DROPDOWN_OPTIONS}
              disabled={disabled}
              hideSearch
              additionalClasses="dark:bg-slate-950 rounded-lg"
              additionalDropdownClasses="dark:bg-slate-950"
              {...getTestProps(testId, 'format', 'testId')}
            />
          )}
        </form.AppField>

        <form.AppField name="code">
          {(field) => (
            <field.Textarea
              additionalTextareaClasses={
                'h-60 mt-4 whitespace-nowrap scrollbar-sm dark:bg-slate-950 resize-none resize-y'
              }
              disabled={disabled}
              {...getTestProps(testId, 'code', 'testId')}
            />
          )}
        </form.AppField>

        <div className="flex gap-3 justify-between mt-4">
          <Button
            buttonColor="borderlessBlue"
            onClick={onRestore}
            disabled={disabled}
            {...getTestProps(testId, 'restore', 'testId')}
          >
            {t('PlainObjectEditorForm.Restore')}
          </Button>
          <div className="flex gap-3 ml-auto">
            <Tooltip
              tooltip={
                objectFormState?.dirty
                  ? t('PlainObjectEditorForm.SaveBeforeCopy')
                  : ''
              }
              phoneTooltipPlacement="topCenter"
              tooltipPlacement="topCenter"
              additionalTooltipClasses="text-base"
            >
              <Button
                buttonColor="blueBordered"
                onClick={onCopy}
                disabled={objectFormState?.dirty || disabled}
                {...getTestProps(testId, 'copy', 'testId')}
              >
                {t('Global.Copy')}
              </Button>
            </Tooltip>
            <Button
              form="raw-object-editor"
              type="submit"
              disabled={disabled}
              {...getTestProps(testId, `save`, 'testId')}
            >
              {t('Global.Save')}
            </Button>
          </div>
        </div>
      </form>
    </form.AppForm>
  );
};

export default PlainObjectEditorForm;
